import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { numFont } from '../../utils/fonts';

// OdometerReading.reading_km / reading_hours are PostgreSQL integers.
const MAX_READING = 2147483647;

export const EMPTY_ODOMETER = { km: '', hours: '' };

const fmt = (n) => n.toLocaleString('fi-FI');

// The "lower than the previous reading" hint waits until typing pauses. Otherwise every
// reading flashes it while its first digits are typed ("14 km is lower than 130 000 km").
const LOWER_HINT_DELAY_MS = 1000;

/** `value` once it has stayed unchanged for `delay` ms. */
function useSettledValue(value, delay) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

const parseField = (raw) => {
  const text = (raw || '').trim();
  if (!text) return { empty: true, valid: true, value: null };
  if (!/^\d+$/.test(text)) return { empty: false, valid: false, value: null };
  const value = parseInt(text, 10);
  // Zero is refused like the backend does: a stored 0 hides the vehicle's real readings.
  return { empty: false, valid: value >= 1 && value <= MAX_READING, value };
};

/**
 * Reading entered in a completion dialog, as the set_state / complete_with_notification body.
 * `filled`: something was typed. `valid`: everything typed is a whole number >= 1.
 */
export function odometerPayload(value) {
  const km = parseField(value?.km);
  const hours = parseField(value?.hours);
  const payload = {};
  if (!km.empty && km.valid) payload.reading_km = km.value;
  if (!hours.empty && hours.valid) payload.reading_hours = hours.value;
  return {
    filled: !km.empty || !hours.empty,
    valid: km.valid && hours.valid,
    payload,
  };
}

/** Whether the dialog may submit with this value: never with a typed but invalid number, and
 * only with a reading when the organization requires one. */
export function odometerAllowsSubmit(value, required) {
  const { filled, valid } = odometerPayload(value);
  return valid && (!required || filled);
}

/**
 * Odometer section of the completion dialogs (CompletionDialog, ConfirmStateChangeDialog).
 * Shown only while the work order has no reading of its own (stateTransitions.needsOdometerPrompt).
 * The previous reading is the vehicle's latest, i.e. from an earlier visit, shown for reference.
 */
function CompletionOdometerField({ maintenance, required, value, onChange, autoFocus = true }) {
  const { t } = useTranslation();
  const previousKm = maintenance?.vehicle_latest_odometer;
  const previousHours = maintenance?.vehicle_latest_hour_meter;
  const previousDate = maintenance?.vehicle?.latest_odometer_date;
  const [showHours, setShowHours] = useState(!!previousHours);

  useEffect(() => {
    setShowHours(!!previousHours);
  }, [maintenance?.id, previousHours]);

  const km = parseField(value?.km);
  const hours = parseField(value?.hours);
  // Shown only while the typed value is the settled one, so the hint also goes away at once
  // when typing resumes.
  const kmSettled = useSettledValue(value?.km ?? '', LOWER_HINT_DELAY_MS) === (value?.km ?? '');
  const hoursSettled = useSettledValue(value?.hours ?? '', LOWER_HINT_DELAY_MS) === (value?.hours ?? '');
  const kmLower = kmSettled && km.value != null && previousKm != null && km.value < previousKm;
  const hoursLower = hoursSettled && hours.value != null && previousHours != null && hours.value < previousHours;
  // Spaces are thousands separators people type ("123 456"); anything else is shown as invalid.
  const setField = (field) => (e) => onChange({ ...value, [field]: e.target.value.replace(/\s/g, '') });

  const previousParts = [
    previousKm != null && `${fmt(previousKm)} km`,
    previousHours != null && `${fmt(previousHours)} h`,
  ].filter(Boolean);

  return (
    <Box
      data-testid="completion-odometer"
      sx={{ textAlign: 'left', border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: 2, mb: 2 }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '15px', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <SpeedIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
        {t('odometerOnCompletion.title')}
      </Typography>

      <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '8px' }} data-testid="completion-odometer-alert">
        {required ? t('odometerOnCompletion.required') : (
          <>
            {t('odometerOnCompletion.missing')} {t('odometerOnCompletion.missingHint')}
          </>
        )}
      </Alert>

      {previousParts.length > 0 && (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
          data-testid="completion-odometer-previous"
        >
          {t('odometerOnCompletion.previous')}:
          <Box component="strong" sx={{ ...numFont }}>{previousParts.join(' / ')}</Box>
          {previousDate && (
            <Box component="span" sx={{ ...numFont }}>({new Date(previousDate).toLocaleDateString('fi-FI')})</Box>
          )}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          autoFocus={autoFocus}
          label={t('odometer.readingKm')}
          value={value?.km ?? ''}
          onChange={setField('km')}
          error={!km.valid}
          helperText={!km.valid ? t('odometer.invalidReading') : (
            kmLower ? t('odometer.warningLower', { new: fmt(km.value), previous: fmt(previousKm) }) : undefined
          )}
          FormHelperTextProps={{ sx: { color: km.valid ? 'warning.dark' : undefined } }}
          inputProps={{ inputMode: 'numeric', 'data-testid': 'completion-odometer-km', style: { ...numFont } }}
          InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
        />
        {showHours ? (
          <TextField
            fullWidth
            size="small"
            label={t('odometer.readingHours')}
            value={value?.hours ?? ''}
            onChange={setField('hours')}
            error={!hours.valid}
            helperText={!hours.valid ? t('odometer.invalidReading') : (
              hoursLower
                ? t('odometer.warningLowerHours', { new: fmt(hours.value), previous: fmt(previousHours) })
                : undefined
            )}
            FormHelperTextProps={{ sx: { color: hours.valid ? 'warning.dark' : undefined } }}
            inputProps={{ inputMode: 'numeric', 'data-testid': 'completion-odometer-hours', style: { ...numFont } }}
            InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
          />
        ) : (
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowHours(true)}
              sx={{ textTransform: 'none' }}
              data-testid="completion-odometer-add-hours"
            >
              {t('odometerOnCompletion.addHours')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CompletionOdometerField;
